import { cn } from "@app/lib/utils";

const STAR_COUNT = 180;
const FIELD_WIDTH = 1600;
const FIELD_HEIGHT = 900;

/**
 * Seeded PRNG. The field is generated once at module scope so the server and
 * the browser produce byte-identical markup — `Math.random()` here would hand
 * Remix a hydration mismatch on every load.
 */
function mulberry32(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;

    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);

    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const STARS = (() => {
  const random = mulberry32(0x513a25);

  return Array.from({ length: STAR_COUNT }, () => {
    const x = random() * FIELD_WIDTH;
    // From `xl` up the heading and its intro copy are overlaid on the leading
    // third of the stage, so the field thins out there rather than competing
    // with body text at `text-white/60`.
    const crowding = x < FIELD_WIDTH * 0.32 ? 0.35 : 1;

    return {
      x,
      y: random() * FIELD_HEIGHT,
      r: 0.6 + random() * 1.3,
      opacity: (0.12 + random() * 0.38) * crowding,
    };
  });
})();

/**
 * The star field behind the globe. It belongs to the section rather than to the
 * sphere, so it does not travel with the selection zoom — the stars are meant to
 * sit still while the planet moves in front of them.
 *
 * Deliberately static. The reference's stars do not twinkle, and the global
 * `.is-ios *` rule in `app/tailwind.css` strips CSS animation outright, so a
 * keyframe here would only ever run on some devices.
 */
export function StarField({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${FIELD_WIDTH} ${FIELD_HEIGHT}`}
      preserveAspectRatio="xMidYMid slice"
      className={cn(
        "pointer-events-none absolute inset-0 h-full w-full",
        className,
      )}
    >
      {STARS.map(({ x, y, r, opacity }, index) => (
        <circle
          key={index}
          cx={x}
          cy={y}
          r={r}
          fill="#dfe9ff"
          opacity={opacity}
        />
      ))}
    </svg>
  );
}
